/**
 * Pending Test Data changes — resolution screen (#1262 slice 4 of
 * #1258).
 *
 * Route: /:workspace/test-data/:setId/bindings/:scriptId/pending
 *
 * Renders the binding's `pendingDiff` entries, grouped by severity
 * (blocking → warning → informational). Each entry has an
 * operator-action affordance suited to its kind + severity:
 *
 *   - blocking remap: <select> of existing column names (operator
 *     picks the new mapping target). Must be resolved before
 *     "Apply changes" enables.
 *   - warning add: text input pre-filled with the suggested column
 *     name (operator accepts or types a different existing column
 *     name to alias against).
 *   - informational remove: single button ("Drop mapping") since
 *     there's no decision — only confirm.
 *
 * Two final actions at the bottom:
 *
 *   - **Apply changes** — POSTs the next-shape mapping to the
 *     pending/apply route. Disabled while any blocking entry is
 *     still unresolved.
 *   - **Discard regeneration** — clears the pending diff without
 *     changing the mapping. The escape hatch — runs proceed
 *     against the existing mapper (and may fail at runtime if a
 *     blocking column is still missing — operator's call).
 *
 * On success, navigates back to the Set page so the operator sees
 * their resolved binding in the "Tests using this Set" tab.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { AlertCircle, AlertTriangle, Info, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import {
  dataBindingsAPI,
  testDataSetsAPI,
  type BindingDiffEntry,
  type BindingPendingDiff,
  type DataBindingDto,
} from '@/lib/api'

interface SetView {
  _id: string
  name: string
  columns: Array<{ name: string }>
}

export default function TestDataBindingPending() {
  const wp = useWorkspacePath()
  const navigate = useNavigate()
  const { setId, scriptId } = useParams<{ setId: string; scriptId: string }>()

  const [binding, setBinding] = useState<DataBindingDto | null>(null)
  const [pending, setPending] = useState<BindingPendingDiff | null>(null)
  const [set, setSet] = useState<SetView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  // Per-field operator resolution. Keyed by field name; value is the
  // column the operator chose to map THAT field to. A "drop mapping"
  // resolution sets the value to the empty string — the apply path
  // then omits the field from the next mapping shape.
  const [resolutions, setResolutions] = useState<Record<string, string>>({})

  // Fetch binding + Set on mount and whenever the route ids change.
  useEffect(() => {
    if (!scriptId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.resolve()
      .then(async () => {
        const b = await dataBindingsAPI.getByScript(scriptId)
        if (!b) throw new Error('No test data is set up for this test.')
        if (cancelled) return
        setBinding(b)
        setPending(b.pendingDiff)
        const setResp = await testDataSetsAPI.get(b.setId ?? setId ?? '')
        if (cancelled) return
        setSet({
          _id: setResp.set._id,
          name: setResp.set.name,
          columns: setResp.set.columns,
        })
        // Resolutions start EMPTY. Warning adds default to
        // "leave unmapped" rather than the suggested column —
        // pre-seeding the suggested name would auto-write a
        // mapping to a column that DOESN'T EXIST on the Set yet,
        // and accidentally clicking Apply would persist a broken
        // mapping. Per Copilot review on #1268: warnings must be
        // opt-in. The operator either picks an existing column,
        // adds the column to the Set first, or leaves the field
        // unmapped (runtime falls through to the script's literal).
        setResolutions({})
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [scriptId, setId])

  const columnNames = useMemo(
    () => (set?.columns ?? []).map((c) => c.name).filter((n) => !n.startsWith('$')),
    [set],
  )

  // Group entries by severity for rendering. The diff service emits
  // a pre-sorted array — the renderer doesn't re-sort, it splits
  // into severity buckets.
  const grouped = useMemo(() => {
    const blocking: BindingDiffEntry[] = []
    const warning: BindingDiffEntry[] = []
    const informational: BindingDiffEntry[] = []
    for (const entry of pending?.entries ?? []) {
      if (entry.severity === 'blocking') blocking.push(entry)
      else if (entry.severity === 'warning') warning.push(entry)
      else informational.push(entry)
    }
    return { blocking, warning, informational }
  }, [pending])

  // Apply is disabled until every blocking entry has a resolution.
  const unresolvedBlocking = useMemo(() => {
    return grouped.blocking.filter((e) => !resolutions[e.field])
  }, [grouped.blocking, resolutions])

  if (loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Loading pending changes…</div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-sm text-destructive mb-2">{error}</div>
        <Link to={`${wp}/test-data`} className="text-sm text-primary hover:underline">
          ← Back to Test Data
        </Link>
      </div>
    )
  }

  if (!binding || !pending) {
    // The binding has no pending changes — the operator landed here
    // from a stale link. Surface the converged state and offer a
    // back-link to the Set.
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground mb-2">
          No changes to review — this test's data is up to date.
        </div>
        {set && (
          <Link to={`${wp}/test-data/${set._id}`} className="text-sm text-primary hover:underline">
            ← Back to Set "{set.name}"
          </Link>
        )}
      </div>
    )
  }

  async function handleApply() {
    if (!binding) return
    setBusy(true)
    setError(null)
    try {
      // Compute the next-shape mapping from the current binding's
      // mappings + the operator's resolutions:
      //   - Every entry in the current map for a field that's
      //     NOT in the diff entries carries through unchanged.
      //   - For each entry, the operator's resolution wins (empty
      //     means "drop").
      const next: Record<string, string> = { ...binding.fieldMappings }
      for (const entry of pending!.entries) {
        const resolved = resolutions[entry.field]
        if (resolved && resolved.length > 0) {
          // Operator picked an existing column — write the mapping.
          next[entry.field] = resolved
        } else if (entry.kind === 'add') {
          // Warning add left empty = "leave unmapped". Make sure we
          // don't carry a stale entry forward (the field shouldn't
          // have been in the existing map, but defense-in-depth).
          delete next[entry.field]
        } else if (entry.kind === 'remove') {
          // Informational remove default — drop the stale mapping.
          delete next[entry.field]
        } else if (entry.kind === 'rename') {
          // Blocking remap with no resolution. Apply is gated by
          // the unresolved-blocking check, so this branch can't
          // execute via the UI — but if it did, keep the stale
          // mapping rather than silently dropping it.
          // (No-op intentional.)
        }
      }
      await dataBindingsAPI.applyPending(binding._id, { nextFieldMappings: next })
      // Navigate back to the Set page after successful apply.
      if (binding.setId) {
        navigate(`${wp}/test-data/${binding.setId}`)
      } else {
        navigate(`${wp}/test-data`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleDiscard() {
    if (!binding) return
    setBusy(true)
    setError(null)
    try {
      await dataBindingsAPI.discardPending(binding._id)
      if (binding.setId) {
        navigate(`${wp}/test-data/${binding.setId}`)
      } else {
        navigate(`${wp}/test-data`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-4">
        <Link
          to={set ? `${wp}/test-data/${set._id}` : `${wp}/test-data`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back to {set?.name ?? 'Test Data'}
        </Link>
      </div>

      <h1 className="text-xl font-semibold mb-2">Review test data changes</h1>
      <p className="text-sm text-muted-foreground mb-6">
        This test was rebuilt and the data it reads changed. Review each change
        below — the ones marked{' '}
        <span className="font-medium text-foreground">Must fix</span> have to be
        sorted out before this test can run.
      </p>

      {grouped.blocking.length > 0 && (
        <Section
          icon={<AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />}
          title="Must fix"
          subtitle="Sort these out before this test can run"
        >
          {grouped.blocking.map((entry) => (
            <BlockingRow
              key={entry.field}
              entry={entry}
              columnNames={columnNames}
              value={resolutions[entry.field] ?? ''}
              onChange={(v) => setResolutions((r) => ({ ...r, [entry.field]: v }))}
            />
          ))}
        </Section>
      )}

      {grouped.warning.length > 0 && (
        <Section
          icon={<AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
          title="Optional"
          subtitle="Runs still work; the data may just not connect"
        >
          {grouped.warning.map((entry) => (
            <WarningRow
              key={entry.field}
              entry={entry}
              columnNames={columnNames}
              /* Empty string = "leave unmapped" — the opt-in
                 default per Copilot review. Operator must
                 explicitly pick an existing column to write a
                 mapping. */
              value={resolutions[entry.field] ?? ''}
              onChange={(v) => setResolutions((r) => ({ ...r, [entry.field]: v }))}
            />
          ))}
        </Section>
      )}

      {grouped.informational.length > 0 && (
        <Section
          icon={<Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
          title="Cleanup"
          subtitle="Old links this test no longer uses"
        >
          {grouped.informational.map((entry) => (
            <InformationalRow key={entry.field} entry={entry} />
          ))}
        </Section>
      )}

      {error && <div className="my-4 text-sm text-destructive">{error}</div>}

      <div className="mt-6 flex items-center justify-between gap-3 border-t pt-4">
        <Button variant="outline" onClick={handleDiscard} disabled={busy}>
          Discard these changes
        </Button>
        <div className="flex items-center gap-2">
          {unresolvedBlocking.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {unresolvedBlocking.length} must-fix change{unresolvedBlocking.length === 1 ? '' : 's'} left
            </span>
          )}
          <Button onClick={handleApply} disabled={busy || unresolvedBlocking.length > 0}>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  )
}

function Section({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  return (
    <section className="mb-6 border rounded">
      <header className="flex items-center gap-2 px-4 py-2 border-b bg-muted/30">
        {icon}
        <div className="font-medium text-sm">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </header>
      <div className="divide-y">{children}</div>
    </section>
  )
}

function BlockingRow({
  entry,
  columnNames,
  value,
  onChange,
}: {
  entry: BindingDiffEntry
  columnNames: string[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="px-4 py-3">
      <div className="text-sm">{entry.message}</div>
      <div className="mt-2 flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Read "{entry.field}" from:</span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="text-sm border rounded px-2 py-1 bg-background"
        >
          <option value="">— pick a column —</option>
          {columnNames.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

function WarningRow({
  entry,
  columnNames,
  value,
  onChange,
}: {
  entry: BindingDiffEntry
  columnNames: string[]
  value: string
  onChange: (v: string) => void
}) {
  // Per Copilot review on #1268: warnings are opt-in. The picker
  // ONLY offers existing columns (a real mapping target) and the
  // "leave unmapped" sentinel (empty string). The suggestion
  // shipped on the entry's `nextColumn` is rendered as guidance
  // text under the picker but isn't a selectable option — selecting
  // it would write a mapping to a column that doesn't exist on the
  // Set, which is the failure mode the review was guarding against.
  // The operator can drop out of the pending-changes flow, add the
  // suggested column to the Set in the matrix editor, then come
  // back and pick it from the now-populated list.
  return (
    <div className="px-4 py-3">
      <div className="text-sm">{entry.message}</div>
      <div className="mt-2 flex items-center gap-2 text-sm flex-wrap">
        <span className="text-muted-foreground">Read "{entry.field}" from:</span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="text-sm border rounded px-2 py-1 bg-background"
        >
          <option value="">— leave unmapped —</option>
          {columnNames.map((n) => (
            <option key={n} value={n}>
              Existing column "{n}"
            </option>
          ))}
        </select>
      </div>
      {entry.nextColumn && !columnNames.includes(entry.nextColumn) && (
        <div className="mt-1 text-xs text-muted-foreground">
          The suggested column "{entry.nextColumn}" isn't on the Set yet — add it to the Set first, then come back and pick it here.
        </div>
      )}
    </div>
  )
}

function InformationalRow({ entry }: { entry: BindingDiffEntry }) {
  return (
    <div className="px-4 py-3">
      <div className="text-sm">{entry.message}</div>
      <div className="mt-1 text-xs text-muted-foreground">
        This test will stop reading "{entry.field}" from "{entry.previousColumn}".
      </div>
    </div>
  )
}
